import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Organization {
  id: string;
  name: string;
  owner_id: string;
  max_employees: number;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: "owner" | "employee";
  is_active: boolean;
  invited_at: string;
  joined_at: string | null;
  created_at: string;
  profile?: {
    full_name: string;
    phone: string;
  };
  email?: string;
}

export function useOrganization() {
  const { user } = useAuth();

  const organizationQuery = useQuery({
    queryKey: ["organization", user?.id],
    queryFn: async (): Promise<Organization | null> => {
      // Primeiro, buscar o organization_id do membro
      const { data: memberData, error: memberError } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user!.id)
        .eq("is_active", true)
        .maybeSingle();

      if (memberError) {
        console.error("Erro ao buscar membro:", memberError);
        return null;
      }

      if (!memberData) {
        return null;
      }

      // Depois, buscar os dados da organização
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", memberData.organization_id)
        .single();

      if (error) {
        console.error("Erro ao buscar organização:", error);
        return null;
      }

      return data;
    },
    enabled: !!user,
  });

  const membershipQuery = useQuery({
    queryKey: ["organization-membership", user?.id],
    queryFn: async (): Promise<OrganizationMember | null> => {
      const { data, error } = await supabase
        .from("organization_members")
        .select("*")
        .eq("user_id", user!.id)
        .eq("is_active", true)
        .maybeSingle();

      if (error) {
        console.error("Erro ao buscar membership:", error);
        throw error;
      }

      return data as OrganizationMember | null;
    },
    enabled: !!user,
    retry: 2,
  });

  const isOwner = membershipQuery.data?.role === "owner";

  return {
    organization: organizationQuery.data,
    membership: membershipQuery.data,
    isOwner,
    isLoading: organizationQuery.isLoading || membershipQuery.isLoading,
    isError: membershipQuery.isError,
  };
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
    }: {
      id: string;
      name: string;
    }) => {
      const { data, error } = await supabase
        .from("organizations")
        .update({ name })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization"] });
      toast.success("Organização atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar organização: " + error.message);
    },
  });
}
